<script setup lang="ts">
/**
 * Aura AGM — AGM Documents (Unaudited, Sole Member individual). 템플릿 docx 를 그대로 재현.
 *   1 DRW · 2 Notice of AGM · 3 Minutes(s.184G) · 4 Statement by Small Company(s.205C) · 5 Solvency Declaration.
 * [[Signers]] 는 AgmSigners(하나로 묶인 서명자). DIRECTORS 반복부(directors)도 이 서명자에서 생성.
 */
import CddVarField from '../cdd/CddVarField.vue';
import AgmSigners from '../cdd/AgmSigners.vue';
import './agmDoc.css';
</script>

<template>
  <div class="agm-doc">
    <!-- ════ 1. DIRECTORS' RESOLUTIONS IN WRITING ════ -->
    <div class="doc-head" style="text-align:center">
      <div style="font-weight:800"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-meta">(Incorporated In Singapore)</div>
      <div class="doc-meta">Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." /></div>
      <div class="doc-meta">(the &ldquo;Company&rdquo;)</div>
    </div>
    <div class="doc-title">DIRECTORS&rsquo; RESOLUTIONS IN WRITING</div>
    <div class="doc-sub">PURSUANT TO THE COMPANY'S CONSTITUTION</div>

    <div class="sec-title">UNAUDITED FINANCIAL STATEMENTS</div>
    <p class="doc-p">Resolved that the unaudited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> together with the directors&rsquo; statement
      thereon be approved and that the said directors&rsquo; statement be signed by any two directors of the Company on behalf
      of the board of directors.</p>

    <div class="sec-title">SPECIAL BUSINESS FOR ANNUAL GENERAL MEETING</div>
    <p class="doc-p">Resolved that in addition to the ordinary business of the Annual General Meeting, the following resolution
      be proposed, by way of a special business, for adoption at the Annual General Meeting.</p>
    <p class="doc-p center"><b>ORDINARY RESOLUTION</b></p>
    <p class="doc-p center"><b>DIRECTORS&rsquo; FEE AND/OR DIRECTORS&rsquo; REMUNERATION</b></p>
    <p class="doc-p">That the directors&rsquo; fee and/or directors&rsquo; remuneration, if any, for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" />, as stated in the financial statements be and
      are hereby approved.</p>

    <div class="sec-title">ANNUAL GENERAL MEETING</div>
    <p class="doc-p">Resolved that any director be authorised to make the necessary arrangements to convene the Annual General
      Meeting of the Company.</p>

    <div class="sec-title">AUTHORITY TO E-FILE RETURNS</div>
    <p class="doc-p">Resolved that any officer of the Company or such other person(s) as directed by the directors be authorised
      to e-file the relevant returns relating thereto with the Accounting and Corporate Regulatory Authority, for and on behalf
      of the Company.</p>
    <p class="doc-p">Dated: <CddVarField field-key="resolution_date" label="Resolution Date" /></p>

    <div class="sec-title">DIRECTORS / SIGNATURES</div>
    <AgmSigners var-key="agm_Signers" board-label="Directors" />

    <!-- ════ 2. NOTICE OF ANNUAL GENERAL MEETING ════ -->
    <div class="doc-head" style="text-align:center;margin-top:24px">
      <div style="font-weight:800"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-meta">(Incorporated In Singapore)</div>
      <div class="doc-meta">Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." /></div>
      <div class="doc-meta">(the &ldquo;Company&rdquo;)</div>
    </div>
    <p class="doc-p">Registered Office: <CddVarField field-key="registered_office_address" label="Registered Office Address" /></p>
    <div class="doc-title">NOTICE OF ANNUAL GENERAL MEETING</div>
    <p class="doc-p">NOTICE IS HEREBY GIVEN that the Annual General Meeting of the Company will be held at:</p>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr><td class="cdd-lcell">Venue</td><td>:&nbsp; <CddVarField field-key="meeting_venue" label="Meeting Venue" /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td>:&nbsp; <CddVarField field-key="meeting_date" label="Meeting Date" /></td></tr>
        <tr><td class="cdd-lcell">Time</td><td>:&nbsp; <CddVarField field-key="meeting_time" label="Meeting Time" /></td></tr>
      </tbody>
    </table>
    <div class="sec-title">AGENDA</div>
    <p class="doc-p">To receive and adopt the unaudited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> together with the directors&rsquo; statement thereon.</p>
    <p class="doc-p">To re-elect director.</p>
    <p class="doc-p">As special business to propose, and if thought fit, to pass the following resolution:-</p>
    <p class="doc-p center"><b>ORDINARY RESOLUTION</b></p>
    <p class="doc-p center"><b>DIRECTORS&rsquo; FEE AND/OR DIRECTORS&rsquo; REMUNERATION</b></p>
    <p class="doc-p">That directors&rsquo; fee and/or directors&rsquo; remuneration, if any, for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" />, as stated in the financial statements be and is hereby approved.</p>
    <p class="doc-p">To transact any other business which may properly be transacted at an Annual General Meeting.</p>
    <p class="doc-p">By Order Of The Board</p>
    <AgmSigners var-key="agm_Signers" board-label="Director" />
    <p class="doc-p">Date: <CddVarField field-key="notice_date" label="Notice Date" /></p>
    <div class="doc-note">
      Notes:<br />
      1. A member entitled to attend and vote at the above meeting is entitled to appoint a proxy to attend and vote instead of him. A proxy need not be a member of the Company.<br />
      2. A corporation which is a member of the Company may, by resolution of its directors, authorise any person to act as its representative at any meetings of the Company, and such representative shall be entitled to exercise the same powers on behalf of the corporation which he represents as if he had been an individual member of the Company.<br />
      3. The instrument appointing a proxy must be deposited at the registered office of the Company not less than <CddVarField field-key="proxy_cut_off" label="Proxy Cut-off (hours)" /> hours before the meeting.
    </div>

    <!-- ════ 3. MINUTES OF ANNUAL GENERAL MEETING (s.184G Sole Member) ════ -->
    <div class="doc-head" style="text-align:center;margin-top:24px">
      <div style="font-weight:800"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-meta">(Incorporated In Singapore)</div>
      <div class="doc-meta">Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." /></div>
      <div class="doc-meta">(the &ldquo;Company&rdquo;)</div>
    </div>
    <div class="doc-title">MINUTES OF ANNUAL GENERAL MEETING</div>
    <p class="doc-p">I, the undersigned, being the Sole Member of the Company beneficially entitled to the whole of the issued shares
      of the Company do hereby pass the following resolution pursuant to Section 184G of the Companies Act 1967 and agree that
      the said resolution shall, for all purposes be as valid and effective as if the same had been passed at the Annual General
      Meeting of the Company duly convened and held on <CddVarField field-key="meeting_date" label="Meeting Date" />.</p>
    <div class="sec-title">UNAUDITED FINANCIAL STATEMENTS</div>
    <p class="doc-p">It was resolved that the unaudited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> together with the directors&rsquo; statement thereon
      be received and adopted.</p>
    <div class="sec-title">SPECIAL BUSINESS</div>
    <p class="doc-p center"><b>ORDINARY RESOLUTION</b></p>
    <p class="doc-p center"><b>DIRECTORS&rsquo; FEE AND/OR DIRECTORS&rsquo; REMUNERATION</b></p>
    <p class="doc-p">It was resolved that the directors&rsquo; fee and/or directors&rsquo; remuneration, if any, for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" />, as stated in the financial statements be and are hereby approved.</p>
    <div class="sec-title">RETIREMENT OF DIRECTOR</div>
    <p class="doc-p">It was resolved that all the Directors who retire in accordance with the Company&rsquo;s Constitution were
      re-elected as Directors of the Company.</p>
    <p class="doc-p">Confirmed by,</p>
    <AgmSigners var-key="agm_member_Signers" board-label="Sole Member" />

    <!-- ════ 4. STATEMENT BY A SMALL COMPANY EXEMPT FROM AUDIT (s.205C) ════ -->
    <div class="doc-title" style="margin-top:24px">STATEMENT BY A SMALL COMPANY</div>
    <div class="doc-sub">EXEMPT FROM AUDIT REQUIREMENTS</div>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr><td class="cdd-lcell">Name of Company</td><td>:&nbsp; <CddVarField field-key="company_name" label="Company Name" /> (the &ldquo;Company&rdquo;)</td></tr>
        <tr><td class="cdd-lcell">Registration No.</td><td>:&nbsp; <CddVarField field-key="company_registration_no" label="Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Financial Year End</td><td>:&nbsp; <CddVarField field-key="financial_year_end" label="Financial Year End" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">We, the directors of the abovementioned company, hereby make the following statement &ndash;</p>
    <p class="doc-p indent">that the Company qualifies as a small company under Section 205C read with the Thirteenth Schedule;</p>
    <p class="doc-p indent">that no notice has been received under Section 205C(2) of the Companies Act in relation to the financial year; and</p>
    <p class="doc-p indent">the accounting and other records required by the Companies Act to be kept by the Company have been kept in accordance with Section 199 of the Companies Act.</p>
    <AgmSigners var-key="agm_Signers" board-label="Director" />
    <p class="doc-p">Dated: <CddVarField field-key="resolution_date" label="Resolution Date" /></p>

    <!-- ════ 5. DECLARATION BY DIRECTOR ON SOLVENCY ════ -->
    <div class="doc-title" style="margin-top:24px">DECLARATION BY DIRECTOR</div>
    <div class="doc-sub">ON SOLVENCY FOR A COMPANY WHICH IS EXEMPT FROM AUDIT REQUIREMENTS</div>
    <table class="cdd-tbl" style="width:auto">
      <tbody>
        <tr><td class="cdd-lcell">Name of Company</td><td>:&nbsp; <CddVarField field-key="company_name" label="Company Name" /> (the &ldquo;Company&rdquo;)</td></tr>
        <tr><td class="cdd-lcell">Registration No.</td><td>:&nbsp; <CddVarField field-key="company_registration_no" label="Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Financial Year End</td><td>:&nbsp; <CddVarField field-key="financial_year_end" label="Financial Year End" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">I/We, the director(s) of the abovementioned company, hereby declare &ndash;</p>
    <p class="doc-p indent">the amount which is due to director, as indicated in the unaudited financial statements for the financial year ended
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> (if any), will not claim such amount from the Company
      or request for repayment of the said amount from the Company during the period of 12 months immediately following the date of this statement.</p>
    <p class="doc-p indent">There are reasonable grounds to believe that the Company will be able to pay its debts as and when they fall due
      during the period of 12 months immediately following the date of this statement.</p>
    <AgmSigners var-key="agm_Signers" board-label="Director" />
    <p class="doc-p">Dated: <CddVarField field-key="resolution_date" label="Resolution Date" /></p>
  </div>
</template>
